import router from 'next/router';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import {
  type RealtimeEvent,
  type RealtimeMessage,
  RealtimeMessageType,
  RealtimeRole,
  CallHoldMessagePayload,
} from '../../../features/realtime/realtime.types';
import { TrackedEvents } from '../../../features/tracking';
import {
  setInitialCameraAccess,
  setInitialMicAccess,
} from '../../../features/tracking/tracking.slice';
import { useTrack } from '../../../hooks/useTrack';
import { getPublicRuntimeConfig } from '../../../libs/init/getPublicRuntimeConfig';
import { type PatientCallService } from '../../../services/call';
import { useAudio } from '../../useAudio';
import { useCallStartSound } from '../../useCallStartSound';
import { useLocalMedia } from '../../useLocalMedia';
import {
  PatientCallHoldSteps,
  patientCallHoldTimer,
} from '../../usePatientCallHold/patientCallHoldTimer';
import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';
import { useService } from '../../useService';
import { CallInitStep, callInitTimer } from './callInitTimer';

export const useCallStateInProgress = (): (() => Promise<void>) => {
  const selectedAudioFile = useCallStartSound();
  const { play: playStartCall } = useAudio(selectedAudioFile);

  const { role, dbSessionId } = useAppSelector(getApolloSessionDetails);
  const dispatch = useAppDispatch();

  const { hasAudioAccess, hasVideoAccess } = useLocalMedia();

  const callService = useService('call');
  const track = useTrack();

  const setInProgress = async (): Promise<void> => {
    // TODO remove
    let unsubscribe: (() => void) | undefined = undefined;

    if (role === RealtimeRole.Patient) {
      const onHoldCallback = (
        message: RealtimeMessage,
        _event: RealtimeEvent,
      ): void => {
        unsubscribe?.();
        const payload = message.payload as CallHoldMessagePayload;

        (callService as PatientCallService)
          .hold(payload.sessionOwnershipToken, dbSessionId)
          .then(async () => {
            track(TrackedEvents.CallHoldLaunched, {});
            patientCallHoldTimer.start(PatientCallHoldSteps.CallHoldLaunched);
            return router.push(getPublicRuntimeConfig().paths.callHold);
          })
          .catch(() => {
            void router.push(getPublicRuntimeConfig().paths.callHold);
          });
      };
      unsubscribe = subscribeToMessage({
        messageType: RealtimeMessageType.CallHold,
        callback: onHoldCallback,
      });
    }

    // This could be `role === RealtimeRole.Patient` but the business logic is for the sound not to play for Providers.
    if (role !== RealtimeRole.Provider) {
      playStartCall();
    }

    callInitTimer.finish(CallInitStep.CallInProgress);

    dispatch(setInitialMicAccess(hasAudioAccess));
    dispatch(setInitialCameraAccess(hasVideoAccess));
  };

  return setInProgress;
};
